import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import Hashids from 'hashids';
import { useAtomValue } from 'jotai';
import Pubnub from 'pubnub';

import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';

import { teacherSubAtom } from '../../../../../showbie/atoms';
import { eventHandlerFactory } from './pubnubMessage';
import { presenceEventHandlerFactory } from './pubnubPresence';

const hashids = new Hashids();

interface UseActivityLiveEventProps {
  roomName: string;
  isRosteredRoom: boolean;
  pubnub: Pubnub;
  activityInstanceId: number;
}

export const useActivityLiveEvent = ({
  roomName,
  pubnub,
  isRosteredRoom,
  activityInstanceId,
}: UseActivityLiveEventProps) => {
  const queryClient = useQueryClient();
  const teacherApplicationContext = useTeacherApplicationContext();
  const teacherSub = useAtomValue(teacherSubAtom);

  const channels = React.useMemo(() => {
    if (
      teacherApplicationContext === TeacherApplicationContext.ShowbieAssessments
    ) {
      const hashedActivityId = hashids.encode(String(activityInstanceId));
      return [
        `s_${teacherSub}_${hashedActivityId}`,
        `t_${teacherSub}_${hashedActivityId}`,
      ];
    } else {
      return [`${roomName}-teacher`, `${roomName}-student`];
    }
  }, [teacherApplicationContext, activityInstanceId, teacherSub, roomName]);

  // Message events
  const messageHandler = React.useMemo<
    (event: Pubnub.MessageEvent) => void
  >(() => {
    return eventHandlerFactory({
      queryClient,
      roomName,
      activityInstanceId: activityInstanceId,
      isRosteredRoom: isRosteredRoom,
      channels: channels,
    });
  }, [queryClient, roomName, activityInstanceId, isRosteredRoom, channels]);

  const ownUuid = pubnub?.getUUID();

  // Presence events
  const presenceHandler = React.useMemo<
    (event: Pubnub.PresenceEvent) => void
  >(() => {
    if (!isRosteredRoom) {
      return null;
    }

    return presenceEventHandlerFactory({
      queryClient: queryClient,
      activityInstanceId: activityInstanceId,
      isRosteredRoom: isRosteredRoom,
      channels: channels,
      ownUuid: ownUuid,
    });
  }, [isRosteredRoom, queryClient, activityInstanceId, channels, ownUuid]);

  const listener = React.useMemo(() => {
    return { message: messageHandler, presence: presenceHandler };
  }, [messageHandler, presenceHandler]);

  React.useEffect(() => {
    pubnub.addListener(listener);
    pubnub.subscribe({
      channels: channels,
      withPresence: true,
    });

    function cleanUp() {
      // remove listeners
      pubnub.removeListener(listener);
      // unsubscribe
      pubnub.unsubscribe({ channels: channels });
    }

    return cleanUp;
  }, [
    roomName,
    listener,
    pubnub,
    isRosteredRoom,
    channels,
    activityInstanceId,
  ]);
};
